// Sidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FaTachometerAlt, FaUsers, FaBoxOpen, FaMapMarkerAlt, FaSignOutAlt,
  FaUser, FaDollarSign, FaReceipt, FaFolder, FaFolderOpen, FaChevronRight, FaChevronLeft,
  FaClipboardList, FaFileInvoiceDollar, FaFileInvoice, FaLandmark, FaLocationArrow,
  FaCode,FaProjectDiagram,FaRoute,FaMapMarkedAlt,FaTruckMoving,FaTruckLoading,FaChartLine,
  FaUndo,
  FaChartBar,
  FaTasks
} from 'react-icons/fa';
import { MdSpaceDashboard } from "react-icons/md";
import { useScreenPermissions } from './useScreenPermissions';

const API_URL = '/api';

const Sidebar = ({ onLogout, collapsed: propCollapsed, onToggle, theme = {} }) => {
  const [openSections, setOpenSections] = useState({});
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [internalCollapsed, setInternalCollapsed] = useState(true);
  const location = useLocation();
  const containerRef = useRef(null);
  const prevCollapsedRef = useRef(propCollapsed !== undefined ? propCollapsed : internalCollapsed);

  const { hasPermission, hasFullAccess, loading: permissionLoading, hasScreenAccess } = useScreenPermissions();

  const collapsed = propCollapsed !== undefined ? propCollapsed : internalCollapsed;

  useEffect(() => {
    const fetchUserAndClient = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userRes = await fetch(`${API_URL}/profile`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const userData = await userRes.json();
        if (userRes.ok && userData.user) {
          setUser(userData.user);
          if (userData.user.Email) {
            const clientsRes = await fetch(`${API_URL}/clients`, {headers:{'Authorization': `Bearer ${token}`}});
            const clientsData = await clientsRes.json();
            if (Array.isArray(clientsData)) {
              const found = clientsData.find(c => c.email_id && c.email_id.toLowerCase() === userData.user.Email.toLowerCase());
              setClient(found || null);
            }
          }
        }
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchUserAndClient();
  }, []);

  useEffect(() => {
    const prevCollapsed = prevCollapsedRef.current;
    prevCollapsedRef.current = collapsed;

    if (prevCollapsed && !collapsed) {
      setOpenSections(prev => {
        const updated = { ...prev };
        filteredSections.forEach(section => {
          if (section.collapsible) {
            const isActive = section.items.some(item => item.to === location.pathname);
            if (isActive) updated[section.label] = true;
          }
        });
        return updated;
      });

      setTimeout(() => {
        try {
          const selector = `[data-sidebar-path="${location.pathname}"]`;
          const el = containerRef.current?.querySelector(selector);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
            el.classList.add('sidebar-active-flash');
            setTimeout(() => el.classList.remove('sidebar-active-flash'), 1000);
          }
        } catch (e) {
          // ignore
        }
      }, 140);
    }
  }, [collapsed, location.pathname]);

  const toggleSection = (label) => setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    if (propCollapsed === undefined) setInternalCollapsed(newCollapsed);
    if (onToggle) onToggle(newCollapsed);
  };

  const handleSectionClick = (label) => {
    if (collapsed) {
      toggleCollapsed();
      setOpenSections(prev => ({ ...prev, [label]: true }));
    } else {
      toggleSection(label);
    }
  };

  const isSectionActive = (section) => {
    if (!section.collapsible) return false;
    return section.items.some(item => location.pathname === item.to);
  };

  const sections = [
    { label: 'Master Data', items: [{ name: 'Dashboard', to: '/dashboard', icon: FaTachometerAlt, screenCode: 'DASHBOARD' }, 

      // { name: 'Dashboard Builder', to: '/dashboard-management', icon:  MdSpaceDashboard, screenCode: 'DASHBOARD' }
    ] },
    {
      label: 'Master',
      collapsible: true,
      icon: FaUsers,
      items: [
        { name: 'Vendor Master', to: '/vendor-master', icon: FaUsers, screenCode: 'VENDOR_MASTER' },
        // { name: 'Customer Master', to: '/customer-master', icon: FaUsers, screenCode: 'CUSTOMER_MASTER' },
        { name: 'Product Master', to: '/product-master', icon: FaBoxOpen, screenCode: 'PRODUCT_MASTER' },
        // { name: 'Bill Of Material', to: '/bill-of-material', icon: FaBoxOpen, screenCode: 'BILL_OF_MATERIAL' },
      
        { name: "Branch Master", to: '/branch-master', icon: FaLocationArrow, screenCode: 'BRANCH_MASTER' },
        { name: 'Location Master', to: '/location-master', icon: FaMapMarkerAlt, screenCode: 'WAREHOUSE' },
        {name: 'Projects', to: '/project-list', icon: FaProjectDiagram, screenCode: 'PROJECT_LIST' },
         {name: 'Feeders', to: '/feeder-list', icon: FaMapMarkedAlt, screenCode: 'FEEDER' },
         {name: 'Activity List', to: '/activity-list', icon: FaTasks, screenCode: 'ACTIVITY_LIST' },
        // { name: 'Department Master', to: '/department-master', icon: FaUsers, screenCode: 'DEPARTMENT_MASTER' },
        { name: 'Users Master', to: '/user-master', icon: FaUsers, screenCode: 'USER_MASTER' },
        // { name: 'RFQ Master', to: '/rfq-items-master', icon: FaClipboardList, screenCode: 'RFQ_MASTER' },
      ],
    },
    {
      label: 'Transaction',
      collapsible: true,
      icon: FaFileInvoiceDollar,
      items: [
        { name: 'Purchase Order ', to: '/purchase-order-items', icon: FaFileInvoiceDollar, screenCode: 'PURCHASE_ORDER' },
        { name: 'Material Receipt Note', to: '/Material-Receipt-Note', icon: FaFileInvoice, screenCode: 'MRN_MASTER' },
        // { name: 'Sales Order', to: '/sales-master', icon: FaFileInvoice, screenCode: 'SALES_ORDER' },
        // { name: 'Delivery Note ', to: '/delivery-note', icon: FaFileInvoice, screenCode: 'DELIVERY_NOTE' },
        // { name: 'Stock In', to: '/stockin', icon: FaBoxOpen, screenCode: 'STOCK_IN' },
        // { name: 'Stock Out', to: '/stockout', icon: FaBoxOpen, screenCode: 'STOCK_OUT' },
        //  { name: 'Production Order', to: '/production-order-master', icon: FaBoxOpen, screenCode: 'PRODUCTION_ORDER' },
        // { name: 'Issue For Production', to: '/issue-for-production', icon: FaBoxOpen, screenCode: 'ISSUE_FOR_PRODUCTION' },
        // { name: 'Receipt From Production', to: '/receipt-from-production', icon: FaBoxOpen, screenCode: 'RECEIPT_FROM_PRODUCTION' },
        // { name: 'Stock Summary Report', to: '/stock-summary-report', icon: FaLandmark, screenCode: 'STOCK_SUMMARY' },
      ],
    },
     {
      label: 'Project',
      collapsible: true,
      icon: FaClipboardList,
      items: [
        
        { name: 'Survey BOQ', to: '/survey-boq', icon: FaRoute, screenCode: 'SURVEY_BOQ' },
        { name: 'Site Indent', to: '/site-indent', icon: FaFileInvoice, screenCode: 'SITE_INDENT' },
        {name:'Material Issue', to:'/material-issue',icon:FaTruckLoading, screenCode: 'MATERIAL_ISSUE'},
        {name:'Material Receive', to:'/material-receive',icon:FaTruckMoving, screenCode: 'MATERIAL_RECEIVE'},
        {name:'Daily Progress Report', to:'/dpr',icon:FaChartLine, screenCode: 'DPR'},
        {name:'Material Return', to:'/material-return',icon:FaUndo, screenCode: 'MATERIAL_RETURN'}
      ],
    },
    { label: 'Report', items: [{ name: 'Comparison Report', to: '/comparison-report', icon: FaChartBar, screenCode: 'COMPARISON_REPORT' },
      
    ] },
    // {
    //   label: 'Finance',
    //   collapsible: true,
    //   icon: FaDollarSign,
    //   items: [
    //     { name: 'Currency Master', to: '/currency-master', icon: FaDollarSign, screenCode: 'CURRENCY_MASTER' },
    //     { name: 'Tax Master', to: '/tax-master', icon: FaReceipt, screenCode: 'TAX_MASTER' },
    //   ],
    // },
    // {
    //   label: 'Catalogs',
    //   collapsible: true,
    //   icon: FaFolder,
    //   items: [
    //     { name: 'Category Master', to: '/category-master', icon: FaFolder, screenCode: 'CATEGORY' },
    //     { name: 'Subcategory Master', to: '/subcategory-master', icon: FaFolderOpen, screenCode: 'SUBCATEGORY' },
    //   ],
    // },
    { label: 'Other', items: [{ name: 'Profile', to: '/profile', icon: FaUser, screenCode: 'PROFILE' },
      // { name: 'Api mapping', to: '/bc-api-mapping', icon: FaCode, screenCode: 'API_MAPPING' },
      
      {name: 'Approval FLow', to : '/approval-flow',icon:FaBoxOpen, screenCode:'APPROVAL_FLOW'}, { name: 'Company Information', to: '/company-information', icon: FaUsers, screenCode: 'COMPANY' }, { name: 'Sessions Service', to: '/session-service', icon: FaUsers, screenCode: 'SESSION_SERVICE' }] },
  ];

  const filterSections = () => {
  if (permissionLoading) return sections;
  if (hasFullAccess) return sections;

  const filtered = sections.map(section => {
    if (!section.collapsible) {
      const filteredItems = section.items.filter(item => {
        const hasAccess = item.screenCode === 'PROFILE' || 
                          item.screenCode === 'DASHBOARD' || 
                          hasScreenAccess(item.screenCode);
        return hasAccess;
      });
      return { ...section, items: filteredItems };
    } else {
      const filteredItems = section.items.filter(item => {
        const hasAccess = item.screenCode === 'PROFILE' || 
                          item.screenCode === 'DASHBOARD' || 
                          hasScreenAccess(item.screenCode);
        return hasAccess;
      });
      if (filteredItems.length > 0) return { ...section, items: filteredItems };
      return null;
    }
  }).filter(s => s !== null);

  return filtered;
};

  const filteredSections = filterSections();

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + (parts[1][0] || '')).toUpperCase();
  };

  // Allow parent to override sidebar colors via `theme` prop (TaxMaster passes pageTheme)
  const defaultColors = {
    primary: '#000000',
    secondary: '#475569',
    muted: '#6b7280',
    border: '#e2e8f0',
    sidebarBorder: '#e2e8f0',
    background: '#ffffff',      // keep sidebar white by default
    pageBackground: '#f8fafc',
    activeBg: '#255437ff', // fallback active background
    activeText: '#ffffff',
    profileBg: '#166534',
    accentHover: '#14532d',
    hoverGray: '#f1f5f9',
    iconColor: '#000000',
    rose600: '#e11d48',
  };

  // Merge incoming theme prop so pages (like TaxMaster) can set activeBg / profileBg etc.
  // IMPORTANT: we intentionally keep sidebar background white (don't override defaultColors.background
  // with theme.sidebarBg) so only the selected file / logout / profile avatar change color.
  const colors = {
    ...defaultColors,
    // theme may include: activeBg, activeText, profileBg
    activeBg: theme.activeBg ?? defaultColors.activeBg,
    activeText: theme.activeText ?? defaultColors.activeText,
    profileBg: theme.profileBg ?? defaultColors.profileBg,
    // leave background as white (defaultColors.background) so the whole sidebar stays white
    background: defaultColors.background,
    // keep other defaults unchanged
    primary: defaultColors.primary,
    secondary: defaultColors.secondary,
    muted: defaultColors.muted,
    border: defaultColors.border,
    sidebarBorder: defaultColors.sidebarBorder,
    pageBackground: defaultColors.pageBackground,
    accentHover: defaultColors.accentHover,
    hoverGray: defaultColors.hoverGray,
    iconColor: defaultColors.iconColor,
    rose600: defaultColors.rose600,
  };

  const textStyles = {
    sectionHeading: 'text-xs font-bold uppercase',
    sectionLabel: 'text-sm font-medium',
    menuItem: 'text-sm font-medium',
    userName: 'text-sm font-semibold',
    userEmail: 'text-xs',
    logout: 'text-sm font-semibold',
  };

  return (
    <>
      <style>{`
        .sidebar-nav-item:hover { background: ${colors.hoverGray}; }
        .sidebar-collapsed-btn:hover { background: ${colors.hoverGray}; }
        .sidebar-logout:hover { background: ${colors.accentHover}; }
        /* small flash animation when auto-scrolling active item */
        .sidebar-active-flash { box-shadow: 0 0 0 3px rgba(0,0,0,0.06); }
      `}</style>

      {collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={toggleCollapsed}
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full shadow-2xl flex flex-col justify-between transition-all duration-300 ${collapsed ? 'w-0 lg:w-16' : 'w-64 lg:w-72'}`}
        style={{ background: colors.background, borderRight: `1.5px solid ${colors.sidebarBorder}` }}
      >
        <button
          className="absolute -right-4 top-6 z-50 bg-white border flex justify-center items-center rounded-full shadow transition-all duration-300"
          style={{ width: 32, height: 32, borderColor: colors.border }}
          onClick={toggleCollapsed}
        >
          {collapsed ? <FaChevronRight style={{ color: colors.muted }} /> : <FaChevronLeft style={{ color: colors.muted }} />}
        </button>

        <div className={`mb-4 flex items-center justify-center ${collapsed ? 'py-4' : 'py-6'}`}>
          {collapsed ? (
            <span className="text-2xl font-bold" style={{ color: colors.primary }}>I</span>
          ) : (
            <h1 className="text-2xl text-center font-bold" style={{ color: colors.primary }}>Inventory Portal</h1>
          )}
        </div>

        <div ref={containerRef} className={`flex-1 overflow-y-auto ${collapsed ? 'px-2' : 'px-4'} scrollbar-hidden flex flex-col items-center`}>
          {filteredSections.map((section) => (
            <div key={section.label} className={`${collapsed ? 'mb-2' : 'mb-3'} w-full ${collapsed ? 'flex flex-col items-center' : ''}`}>
              {!collapsed && (
                <div className={`px-3 py-2 ${textStyles.sectionHeading}`} style={{ color: colors.secondary }}>
                  {section.label}
                </div>
              )}

              {section.collapsible ? (
                collapsed ? (
                  <button
                    onClick={() => handleSectionClick(section.label)}
                    className="flex items-center justify-center p-2 rounded w-full transition-all duration-200 sidebar-collapsed-btn"
                    style={{
                      background: isSectionActive(section) ? colors.activeBg : 'transparent',
                      color: isSectionActive(section) ? colors.activeText : colors.primary
                    }}
                    title={section.label}
                  >
                    <section.icon className="text-lg" style={{ color: isSectionActive(section) ? colors.activeText : colors.primary }} />
                  </button>
                ) : (
                  <>

                    <button
                      onClick={() => toggleSection(section.label)}
                      className={`w-full text-left px-3 py-2 rounded transition flex justify-between items-center`}
                      style={{
                        color: colors.primary,
                        background: openSections[section.label] ? colors.hoverGray : 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <section.icon className="text-lg" style={{ color: colors.primary }} />
                        <span className={textStyles.sectionLabel} style={{ color: colors.primary }}>{section.label}</span>
                      </div>
                      <span className="text-xs" style={{ color: colors.muted }}>
                        {openSections[section.label] ? '▲' : '▼'}
                      </span>
                    </button>

                    <div className={`transition-all duration-300 overflow-hidden ${openSections[section.label] ? 'max-h-screen mt-2' : 'max-h-0'} pl-3`}>
                      {section.items.map(({ name, to, icon: Icon }) => (
                        <NavLink
                          key={name}
                          to={to}
                          data-sidebar-path={to}
                          className="flex items-center gap-3 px-3 py-2 rounded transition-colors duration-200 sidebar-nav-item"
                          style={({ isActive }) => (({
                            background: isActive ? colors.activeBg : 'transparent',
                            color: isActive ? colors.activeText : colors.primary,
                          }))}
                          end
                        >
                          {({ isActive }) => (
                            <>
                              <Icon className="text-lg" style={{ color: isActive ? colors.activeText : colors.muted }} />
                              <span className={textStyles.menuItem}>{name}</span>
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </>
                )
              ) : (
                <>
                  {section.items.map(({ name, to, icon: Icon }) => (
                    <NavLink
                      key={name}
                      to={to}
                      data-sidebar-path={to}
                      className="flex items-center gap-3 px-3 py-2 rounded transition-colors duration-200 justify-start sidebar-nav-item"
                      style={({ isActive }) => (({
                        background: isActive ? colors.activeBg : 'transparent',
                        color: isActive ? colors.activeText : colors.primary,
                      }))}
                      end
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className="text-lg" style={{ color: isActive ? colors.activeText : colors.primary }} />
                          {!collapsed && <span className={textStyles.menuItem}>{name}</span>}
                        </>
                      )}
                    </NavLink>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>

        <div className={`border-t pt-4 ${collapsed ? 'flex flex-col items-center' : ''}`} style={{ borderColor: colors.border }}>
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''} p-3 rounded-lg mb-3`} style={{ background: colors.background }}>
            {loading ? (
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
            ) : client && client.client_id ? (
              <img
                src={`/api/clients/${client.client_id}/logo?${Date.now()}`}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                alt="user"
                style={{ background: colors.profileBg }} // ensure profile logo area still uses profileBg if image missing or overlay
              />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow"
                style={{ background: colors.profileBg, color: colors.activeText }}>
                {getInitials(user?.FullName || user?.Username || 'U')}
              </div>
            )}
            {!collapsed && (
              <div className="text-sm">
                <p className={textStyles.userName} style={{ color: colors.primary }}>
                  {user?.FullName || user?.Username || 'User'}
                </p>
                <p className={textStyles.userEmail} style={{ color: colors.secondary }}>
                  {user?.Email || '-'}
                </p>
              </div>
            )}
          </div>

          {!collapsed && (
            <button
              onClick={async () => {
  try {
    const token = localStorage.getItem('authToken');

    if (token) {
      await fetch(`${API_URL}/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    }
  } catch (err) {
    console.error('Logout API error:', err);
  } finally {
    // always clear local session
    localStorage.removeItem('authToken');

    // optional: clear other storage too
    sessionStorage.clear();

    onLogout && onLogout();
  }
}}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors duration-200 sidebar-logout"
              style={{ background: colors.profileBg, color: colors.activeText }}
            >
              <FaSignOutAlt style={{ color: colors.activeText }} />
              <span className={textStyles.logout} style={{ color: colors.activeText }}>Logout</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
